import { button } from "../ui.js";
import { resetAll } from "../../shared/settings-store.js";
import { FAVORITES_KEY, HISTORY_KEY, SESSION_KEY, SESSION_OFFER_KEY } from "../../shared/settings-schema.js";
import { remove } from "../../shared/store.js";
import { clearWallpaperImage } from "../../shared/wallpaper-image.js";
import { iconStore } from "../../shared/site-icons.js";
import { downloadJSON, pickJSONFile, collectSave, wipeEverything, reloadApp } from "../../shared/backup.js";
import { privacyCard } from "../../shared/privacy-copy.js";
import { POS_KEY } from "../../shared/read-core.js";
import { noteSafe } from "../../shared/health-gate.js";
import { UPLOAD_DBS, isUploadDb, isModelCache, UPLOAD_INDEX_KEYS } from "../../shared/uploads.js";
import { count as heldTally, dropAll as dropHeld, heldWords } from "../../shared/carry-held.js";

// What worked per site (shared/site-ways.js, and the older "rv:engine-hosts" list): about visited
// sites, so it goes with history.
const ENGINE_HOSTS_KEY = "rv:engine-hosts";
const SITE_KEYS = [ENGINE_HOSTS_KEY, "rv:site-ways", "rv:site-salt"];
function forgetEngineHosts() {
	for (const k of SITE_KEYS) { try { localStorage.removeItem(k); } catch { /* best effort */ } }
	// When websites open on the separate sandbox address (Phase 1), that list lives there: the app
	// window forwards this (app/engine-bridge.js). Nothing listens in the default mode.
	try { if (window.parent !== window) window.parent.postMessage({ type: "revault-forget-engine-hosts" }, location.origin); } catch { /* best effort */ }
}

// The engines' own databases stay open while Re:Vault runs, so deleting them is blocked (and a
// pending delete would stall the next start). Their site data (cookies, redirects) is emptied in
// place instead; their config is kept so open pages keep working. Every other database (the sites'
// own storage, Re:Vault's caches) is emptied and deleted, except the student's own uploads and files
// (shared/uploads.js), which have their own row.
const ENGINE_DBS = { "$scramjet": ["config", "publicSuffixList"], "__scramjet_controller": [], "__op": [] };
function emptyDatabase(name, keep) {
	return new Promise((res) => {
		let req;
		try { req = indexedDB.open(name); } catch { return res(); }
		req.onupgradeneeded = () => { try { req.transaction.abort(); } catch { /* best effort */ } }; // never create one
		req.onerror = () => res();
		req.onblocked = () => res();
		req.onsuccess = () => {
			const db = req.result;
			try {
				const names = [...db.objectStoreNames].filter((n) => !(keep || []).includes(n));
				if (!names.length) { db.close(); return res(); }
				const tx = db.transaction(names, "readwrite");
				names.forEach((n) => tx.objectStore(n).clear());
				tx.oncomplete = tx.onabort = tx.onerror = () => { try { db.close(); } catch { /* best effort */ } res(); };
			} catch { try { db.close(); } catch { /* closed */ } res(); }
		};
	});
}
function deleteDatabase(name) {
	return new Promise((res) => {
		let req;
		try { req = indexedDB.deleteDatabase(name); } catch { return res("done"); }
		req.onsuccess = () => res("done");
		req.onerror = () => res("done");
		req.onblocked = () => res("blocked"); // finishes by itself once the engines close it
	});
}

// Ask the Re:Vault window to empty the engines' live cookie jars (app/engines.js answers a /settings
// frame and calls its clearSiteCookies). If the engines never loaded there is nothing to clear. Resolves when it is done, or after 3 s if no app window answers.
function clearAppCookies() {
	return new Promise((res) => {
		let top = null;
		try { top = window.parent !== window ? window.parent : null; } catch { top = null; }
		if (!top) return res();
		const ch = new MessageChannel();
		const t = setTimeout(() => { ch.port1.close(); res(); }, 3000);
		ch.port1.onmessage = () => { clearTimeout(t); ch.port1.close(); res(); };
		try { top.postMessage({ type: "revault-clear-site-cookies" }, location.origin, [ch.port2]); } catch { clearTimeout(t); res(); }
	});
}

// How many carry copies wait on this device, or -1 when that can't be read (the confirm then says
// "any carry copies", since some may be there).
async function heldCount() { try { return await heldTally(); } catch { return -1; } }

async function clearData() {
	// A carry code's last open that wasn't added yet lives in a database this would delete.
	const held = await heldCount();
	if (held && !confirm("Clear site data also throws away " + heldWords(held) + ". Clear anyway?")) return;
	// The engines' live cookie jars (memory) first, so nothing writes the old cookies back.
	await clearAppCookies();
	try {
		if (window.caches) for (const k of await caches.keys()) if (!isModelCache(k)) await caches.delete(k);
	} catch (e) {}
	let blocked = false;
	try {
		if (window.indexedDB && indexedDB.databases) {
			const dbs = (await indexedDB.databases()).filter((d) => d.name && !isUploadDb(d.name));
			const own = (n) => Object.prototype.hasOwnProperty.call(ENGINE_DBS, n);
			for (const d of dbs) await emptyDatabase(d.name, own(d.name) ? ENGINE_DBS[d.name] : null);
			for (const d of dbs) if (!own(d.name) && (await deleteDatabase(d.name)) === "blocked") blocked = true;
		}
	} catch (e) {}
	if (held) await dropHeld(); // and its note for the start page
	try {
		document.cookie.split(";").forEach((c) => {
			const name = c.split("=")[0].trim();
			if (name) document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
		});
	} catch (e) {}
	forgetEngineHosts();
	alert(blocked ? "Site data cleared. Pages that are open now may keep what they already loaded until you reload them." : "Site data cleared.");
}

// Music, GIF and video wallpapers, Linux machines, Study Helper chats and desktop files.
async function deleteUploads() {
	if (!confirm("Delete your uploads and files? Music, GIF and video wallpapers, Linux machines, Study Helper chats and desktop files on this device are erased. This can't be undone.")) return;
	let names = UPLOAD_DBS;
	try { if (window.indexedDB && indexedDB.databases) names = (await indexedDB.databases()).map((d) => d.name).filter((n) => n && isUploadDb(n)); } catch (e) {}
	let blocked = false;
	for (const n of names) if ((await deleteDatabase(n)) === "blocked") blocked = true;
	for (const k of UPLOAD_INDEX_KEYS) remove(k);
	alert(blocked ? "Deleted. Something that is open now (a Linux machine or the desktop) finishes deleting when you close it." : "Your uploads and files were deleted.");
}

async function unregisterSW() {
	try {
		const regs = await navigator.serviceWorker.getRegistrations();
		for (const r of regs) await r.unregister();
		alert("Done. Reload Re:Vault and it sets itself up fresh.");
	} catch (e) {}
}

// The merge rules (shared/merge-save.js), a lazy chunk shared with the carry sheet and the home card.
let mergeUrl = "";
try { mergeUrl = __RVM.mergeJs; } catch { /* unit tests have no build defines */ }
const plural = (n, one, many) => n + " " + (n === 1 ? one : many);

// What the confirm says the file holds: "3 decks, 6 notes and 12 study days".
export function fileSummary(s) {
	const parts = [];
	if (s.decks) parts.push(plural(s.decks, "deck", "decks"));
	if (s.notes) parts.push(plural(s.notes, "note", "notes"));
	if (s.days) parts.push(plural(s.days, "study day", "study days"));
	if (!parts.length) return "";
	return parts.length === 1 ? parts[0] : parts.slice(0, -1).join(", ") + " and " + parts[parts.length - 1];
}

// Brings a save file into this browser the same way a carry code does: combined with what's here,
// never replacing it. Then reloads onto it.
function restore() {
	pickJSONFile(async (obj, err) => {
		const bad = () => alert("That isn’t a valid Re:Vault backup file.");
		if (err || !obj || obj._rv !== "save" || !obj.data || typeof obj.data !== "object" || Array.isArray(obj.data)) return bad();
		let m;
		try { m = await import(mergeUrl); } catch { alert("Couldn't load that just now. Check your connection and try again."); return; }
		const dry = m.mergeSave(obj, { scope: "file", dryRun: true });
		if (!dry.ok) return bad();
		if (!dry.written.length) { alert("Everything in that file is already in this browser. Nothing changed."); return; }
		const what = fileSummary(dry.summary);
		if (!confirm("Add this file's saves to this browser?" + (what ? " It has " + what + "." : "") +
			" Your work here is kept: matching items are combined, and settings, looks, saved tabs and a wallpaper you already have stay as they are. Then Re:Vault reloads.")) return;
		const res = m.mergeSave(obj, { scope: "file" });
		if (!res.ok) alert("Some of it didn't fit in this browser. Remove a picture wallpaper, then restore the file again. Anything already added stays.");
		if (!res.written.length) return;
		await noteSafe("restore");
		reloadApp();
	});
}

// Complete destruction - wipe ALL local data, then reload a clean slate.
async function deleteEverything() {
	const held = await heldCount();
	if (!confirm("Erase everything on this device: study work, settings, music, wallpapers, Linux machines, Helper chats and desktop files? " +
		(held ? "It also throws away " + heldWords(held) + ". " : "") + "This can't be undone.")) return;
	await wipeEverything();
	if (held) await dropHeld(); // and its note for the start page
	reloadApp();
}

// The privacy card heads Privacy and safety; the /privacy link opens as a tab through embed.js.
export const privacyInfo = () => privacyCard(document, "rv-privcard");

export const privacyActions = {
	// Read Aloud's "where I stopped" list holds page addresses too, so it goes with history.
	clearHistory: () => button("Clear history", async () => { if (confirm("Clear all history?")) { remove(HISTORY_KEY); forgetEngineHosts(); try { localStorage.removeItem(POS_KEY); } catch { /* storage off */ } await iconStore().clear(); alert("History cleared."); } }, "rv-btn-danger"),
	clearTabs: () => button("Clear saved tabs", () => { remove(SESSION_KEY); remove(SESSION_OFFER_KEY); alert("Saved tabs cleared."); }, "rv-btn-danger"),
	clearFavorites: () => button("Clear favorites", () => { if (confirm("Remove all favorites?")) { remove(FAVORITES_KEY); alert("Favorites cleared."); } }, "rv-btn-danger"),
	clearSiteData: () => button("Clear site data", clearData, "rv-btn-danger"),
};

export const backupActions = {
	backupFile: () => button("Download", () => { downloadJSON("revault-backup.json", collectSave()); noteSafe("file"); }),
	restoreFile: () => button("Choose file…", restore),
};

export const troubleActions = {
	fixLoading: () => button("Fix loading problems", unregisterSW),
	deleteUploads: () => button("Delete uploads and files", deleteUploads, "rv-btn-danger"),
	resetAll: () => button("Reset all settings", () => { if (confirm("Reset all settings to their defaults? Favorites, history and saved looks stay.")) { resetAll(); clearWallpaperImage(); location.reload(); } }, "rv-btn-danger"),
	deleteAll: () => button("Delete everything", deleteEverything, "rv-btn-danger"),
};
